import React from "react"
import { MapPin, Eye, Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { AssigneePicker } from "./AssigneePicker"
import { PROJECT_TYPE_MAP, PROJECT_STATUS_MAP } from "../constants"

function TypeBadge({ type }) {
  const t = PROJECT_TYPE_MAP[type]
  if (!t) return <span className="text-slate-400">—</span>
  return <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-medium ${t.badge}`}>{t.label}</span>
}

function StatusBadge({ status }) {
  const s = PROJECT_STATUS_MAP[status]
  if (!s) return <span className="text-slate-400">—</span>
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] text-slate-700">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.dotColor }} />
      {s.label}
    </span>
  )
}

export function ProjectsTable({ projects = [], onView, onEdit, onDelete, onAssign, assigningId }) {
  const [sortField, setSortField] = React.useState(null)
  const [sortDirection, setSortDirection] = React.useState(null)

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const sortedProjects = React.useMemo(() => {
    if (!sortField || !sortDirection) return projects
    return [...projects].sort((a, b) => {
      let aVal, bVal
      if (sortField === "name") {
        aVal = a.name || ""
        bVal = b.name || ""
      } else if (sortField === "assigned_to") {
        aVal = a.assigned_to?.name || ""
        bVal = b.assigned_to?.name || ""
      } else if (sortField === "type") {
        aVal = a.project_type || ""
        bVal = b.project_type || ""
      } else if (sortField === "status") {
        aVal = a.status || ""
        bVal = b.status || ""
      } else if (sortField === "inventory") {
        aVal = a.units_total || 0
        bVal = b.units_total || 0
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [projects, sortField, sortDirection])

  const renderSortableHeader = (label, field, className = "") => {
    const isSorted = sortField === field
    return (
      <button
        onClick={() => handleSort(field)}
        className={`inline-flex items-center gap-1 hover:text-black focus:outline-none font-semibold text-black ${className}`}
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </button>
    )
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead className="text-black">{renderSortableHeader("Project", "name")}</TableHead>
            <TableHead className="text-black">{renderSortableHeader("Assigned to", "assigned_to")}</TableHead>
            <TableHead className="text-black">{renderSortableHeader("Type", "type")}</TableHead>
            <TableHead className="text-black">{renderSortableHeader("Status", "status")}</TableHead>
            <TableHead className="text-right text-black">{renderSortableHeader("Inventory", "inventory", "w-full justify-end")}</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedProjects.map((p, idx) => (
            <TableRow key={p.id} className="group">
              <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
              <TableCell>
                <div className="text-left">
                  <span className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 text-[13px]">{p.name}</span>
                    {!p.active && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                        Inactive
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1 text-[12px] text-slate-400">
                    {p.code && <span className="font-mono uppercase">{p.code}</span>}
                    {p.code && (p.city || p.state) && <span>·</span>}
                    {(p.city || p.state) && (
                      <span className="flex items-center gap-0.5">
                        <MapPin className="h-3 w-3" />
                        {[p.city, p.state].filter(Boolean).join(", ")}
                      </span>
                    )}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <AssigneePicker
                  assignedTo={p.assigned_to}
                  isPending={assigningId === p.id}
                  onAssign={(membershipId) => onAssign(p, membershipId)}
                />
              </TableCell>
              <TableCell>
                <TypeBadge type={p.project_type} />
              </TableCell>
              <TableCell>
                <StatusBadge status={p.status} />
              </TableCell>
              <TableCell className="text-right text-slate-500">
                {p.units_total || 0} unit{p.units_total === 1 ? "" : "s"}
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-0.5">
                  <Button variant="ghost" size="icon" onClick={() => onView(p)} title="View units">
                    <Eye className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onEdit(p)} title="Edit project">
                    <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(p)} title="Delete project">
                    <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
